import { Component, OnInit, OnDestroy } from '@angular/core';
import { GoogleMapsLoaderService } from './loader-service';
import { Poi } from './models/poi.model';
import { AudioMode, AudioTrack } from './models/audio.track.model';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../environments/environment';

declare const google: any;

export type LocationStatus = 'waiting' | 'ok' | 'denied' | 'unavailable' | 'timeout';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {

  // ---------- MAP ----------
  map!: google.maps.Map;
  userMarker?: google.maps.Marker;
  poiMarkers: google.maps.Marker[] = [];
  poiIcon!: google.maps.Icon;

  // ---------- UI ----------
  sidebarOpen = false;
  activePoi?: Poi;
  locationStatus: LocationStatus = 'waiting';
  locationAccuracyLow = false;

  // ---------- AUDIO ----------
  private player = new Audio();
  currentTrack?: AudioTrack;
  isPlaying = false;

  // ---------- GEO ----------
  private watchId?: number;
  private static readonly EXIT_BUFFER = 5; // hysteresis: exit 5m past entry radius

  // ---------- LANGUAGE ----------
  get selectedLanguage(): string {
    return localStorage.getItem('language') || 'hr';
  }

  // ---------- POIs ----------
  pois: Poi[] = [
    {
      id: 1, title: 'Cesta Pod Morem Zaljeva Burle', description: '', lat: 44.820511, lng: 13.924665, radius: 15,
      audios: [{ id: '1', title: '1.mp4', src: 'assets/sounds/1.mp4', mode: 'normal' }]
    },
    {
      id: 2, title: 'Impluvij', description: '', lat: 44.820201, lng: 13.922344, radius: 15,
      audios: [{ id: '2', title: '2.mp4', src: 'assets/sounds/2.mp4', mode: 'normal' }]
    },
    {
      id: 3, title: 'Hippokaust', description: '', lat: 44.819927, lng: 13.922087, radius: 15,
      audios: [{ id: '3', title: '3.mp4', src: 'assets/sounds/3.mp4', mode: 'normal' }]
    },
    {
      id: 4, title: 'Maritimna Vila Za Odmor', description: '', lat: 44.818847, lng: 13.921857, radius: 15,
      audios: [{ id: '4', title: '4.mp4', src: 'assets/sounds/4.mp4', mode: 'normal' }]
    },
    {
      id: 5, title: 'Apsida Kraj Objekta B', description: '', lat: 44.818687341763535, lng: 13.92202726473935, radius: 15,
      audios: [{ id: '5', title: '5.mp4', src: 'assets/sounds/5.mp4', mode: 'normal' }]
    },
    {
      id: 6, title: 'Mozaici U Prostoriji', description: '', lat: 44.818398, lng: 13.921925, radius: 15,
      audios: [{ id: '6', title: '6.mp4', src: 'assets/sounds/6.mp4', mode: 'normal' }]
    },
    {
      id: 7, title: 'Vodospreme', description: '', lat: 44.818223, lng: 13.921561, radius: 15,
      audios: [{ id: '7', title: '7.mp4', src: 'assets/sounds/7.mp4', mode: 'normal' }]
    },
    {
      id: 8, title: 'Kuhinjska Pec', description: '', lat: 44.816301, lng: 13.922446, radius: 15,
      audios: [{ id: '8', title: '8.mp4', src: 'assets/sounds/8.mp4', mode: 'normal' }]
    },
    {
      id: 9, title: 'Mlinsko Kamenje Prerada Maslina', description: '', lat: 44.816225, lng: 13.922762, radius: 15,
      audios: [{ id: '9', title: '9.mp4', src: 'assets/sounds/9.mp4', mode: 'normal' }]
    },
    {
      id: 10, title: 'Pristanište', description: '', lat: 44.816317, lng: 13.923572, radius: 15,
      audios: [{ id: '10', title: '10.mp4', src: 'assets/sounds/10.mp4', mode: 'normal' }]
    },
    {
      id: 11, title: 'Vila Rustika', description: '', lat: 44.816583, lng: 13.927158, radius: 15,
      audios: [{ id: '11', title: '11.mp4', src: 'assets/sounds/11.mp4', mode: 'normal' }]
    },
    {
      id: 12, title: 'Vila Rustika Skladište', description: '', lat: 44.816596, lng: 13.927270, radius: 15,
      audios: [{ id: '12', title: '12.mp4', src: 'assets/sounds/12.mp4', mode: 'normal' }]
    },
    {
      id: 13, title: 'Nekopola', description: '', lat: 44.819442, lng: 13.928721, radius: 15,
      audios: [{ id: '13', title: '13.mp4', src: 'assets/sounds/13.mp4', mode: 'normal' }]
    },
    {
      id: 14, title: 'Nekopola', description: '', lat: 44.819559, lng: 13.928709, radius: 15,
      audios: [{ id: '14', title: '14.mp4', src: 'assets/sounds/14.mp4', mode: 'normal' }]
    },
    {
      id: 15, title: 'Rituali Rimljanki', description: '', lat: 44.819550, lng: 13.928348, radius: 15,
      audios: [{ id: '15', title: '15.mp4', src: 'assets/sounds/15.mp4', mode: 'normal' }]
    }
  ];

  private readonly MAP_STYLE: google.maps.MapTypeStyle[] = [
    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', stylers: [{ visibility: 'off' }] },
    { featureType: 'administrative', stylers: [{ visibility: 'off' }] },
    { featureType: 'road', elementType: 'labels', stylers: [{ visibility: 'off' }] }
  ];

  constructor(
    private mapsLoader: GoogleMapsLoaderService,
    private translate: TranslateService
  ) { }

  ngOnInit(): void {
    const lang = this.selectedLanguage;
    this.translate.addLangs(['hr', 'en', 'it', 'de']);
    this.translate.setDefaultLang('hr');
    this.translate.use(lang);

    this.mapsLoader.load(environment.googleMapsApiKey).then(() => {
      this.initPoiIcon();
      this.initMap();
      this.addPoiMarkers();
      this.watchUserLocation();
      this.bindPlayerEvents();
    });
  }

  ngOnDestroy(): void {
    if (this.watchId != null) navigator.geolocation.clearWatch(this.watchId);
    this.stopAudio();
  }

  // ---------- MAP ----------
  private initPoiIcon(): void {
    this.poiIcon = {
      url: 'assets/pins/poi.png',
      scaledSize: new google.maps.Size(57, 67),
      anchor: new google.maps.Point(28.5, 67)
    };
  }

  private initMap(): void {
    const el = document.getElementById('map') as HTMLElement;
    if (!el) return;

    this.map = new google.maps.Map(el, {
      center: { lat: 44.8189, lng: 13.9249 },
      zoom: 16,
      minZoom: 0,
      maxZoom: 20,
      disableDefaultUI: true,
      styles: this.MAP_STYLE
    });
  }

  private addPoiMarkers(): void {
    this.pois.forEach(poi => {
      const marker = new google.maps.Marker({
        position: { lat: poi.lat, lng: poi.lng },
        map: this.map,
        icon: this.poiIcon
      });
      marker.addListener('click', () => this.openPoi(poi));
      this.poiMarkers.push(marker);
    });
  }

  // ---------- GEOLOCATION ----------
  private watchUserLocation(): void {
    if (!navigator.geolocation) {
      this.locationStatus = 'unavailable';
      return;
    }

    this.watchId = navigator.geolocation.watchPosition(
      pos => {
        const { latitude, longitude, accuracy } = pos.coords;

        this.locationStatus = 'ok';
        this.locationAccuracyLow = accuracy > 25;

        const position = { lat: latitude, lng: longitude };

        if (!this.userMarker) {
          this.userMarker = new google.maps.Marker({
            position,
            map: this.map,
            icon: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png'
          });
        } else {
          this.userMarker.setPosition(position);
        }

        this.keepUserInView(position);

        // Skip proximity check only when accuracy is very poor
        if (accuracy <= 30) {
          this.checkProximity(latitude, longitude);
        }
      },
      err => {
        switch (err.code) {
          case GeolocationPositionError.PERMISSION_DENIED:
            this.locationStatus = 'denied';
            break;
          case GeolocationPositionError.POSITION_UNAVAILABLE:
            this.locationStatus = 'unavailable';
            break;
          case GeolocationPositionError.TIMEOUT:
            this.locationStatus = 'timeout';
            break;
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0
      }
    );
  }

  private keepUserInView(pos: google.maps.LatLngLiteral): void {
    const bounds = this.map.getBounds();
    if (bounds && !bounds.contains(pos)) {
      this.map.panTo(pos);
    }
  }

  // ---------- POI ----------
  openPoi(poi: Poi): void {
    this.activePoi = poi;
  }

  closePoi(): void {
    this.activePoi = undefined;
    this.stopAudio();
  }

  togglePlay(track: AudioTrack): void {
    const file = track.src.split('/').pop();
    const src = `assets/sounds/${this.selectedLanguage}/${file}`;

    if (this.currentTrack?.id === track.id) {
      if (this.isPlaying) {
        this.player.pause();
        this.isPlaying = false;
      } else {
        this.player.play().catch(() => {});
        this.isPlaying = true;
      }
      return;
    }

    this.stopAudio();

    this.currentTrack = track;
    this.player.src = src;
    this.player.currentTime = 0;

    this.player.play().catch(() => {});
    this.isPlaying = true;
  }

  private stopAudio(): void {
    this.player.pause();
    this.player.currentTime = 0;
    this.isPlaying = false;
    this.currentTrack = undefined;
  }

  private bindPlayerEvents(): void {
    this.player.onended = () => {
      this.isPlaying = false;
      this.currentTrack = undefined;
    };
  }

  startOver(track: AudioTrack): void {
    if (this.currentTrack?.id !== track.id) return;

    this.player.currentTime = 0;
    this.player.play().catch(() => {});
    this.isPlaying = true;
  }

  // ---------- PROXIMITY ----------
  private checkProximity(lat: number, lng: number): void {
    for (const poi of this.pois) {
      const dist = this.distance(lat, lng, poi.lat, poi.lng);
      // Hysteresis: active POI stays open until user is EXIT_BUFFER metres past radius
      const threshold = poi.radius + (this.activePoi?.id === poi.id ? AppComponent.EXIT_BUFFER : 0);

      if (dist <= threshold) {
        if (!this.activePoi || this.activePoi.id !== poi.id) {
          this.openPoi(poi);
        }
        return;
      }
    }
    this.closePoi();
  }

  private distance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371000;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(lat1 * Math.PI / 180) *
      Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  openSidebar(): void {
    this.sidebarOpen = true;
  }

  closeSidebar(): void {
    this.sidebarOpen = false;
  }

  get audioMode(): AudioMode {
    const v = localStorage.getItem('audioMode') as AudioMode | null;
    return v === 'normal' || v === 'archaeology' || v === 'all'
      ? v
      : 'all';
  }

  get visibleTracks(): AudioTrack[] {
    if (!this.activePoi) return [];

    if (this.audioMode === 'all') {
      return this.activePoi.audios;
    }

    return this.activePoi.audios.filter(
      track => track.mode === this.audioMode
    );
  }
}
